import { Component, inject, input, Input, OnChanges, OnInit, SimpleChanges, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { FilterInput } from '@it2go/data-grid';
import { WorkTypeEnum } from '@libs/types/src/common/enum/work-type.enum';
import {
    dateFromColumn,
    dayDateFromColumn,
    filesColumn,
    folderPersonColumn,
    usersChipColumn,
} from '../../../../shared/components/kit/table/table.fields';
import { TableColumn, TableColumnType } from '../../../../shared/components/kit/table/table.types';
import { WorkGridItemObject } from '@it2go/types';
import { Router } from '@angular/router';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { ConfirmBuilder } from '../../../../shared/service/confirm.builder';
import { workUri } from 'packages/client/src/app/utils/router.utils';
import { SpecialistRecordListStore } from './specialist-record-list.store';
import { workHideDelete } from '../../../../../components/medical-service/work/work-detail/utils/work.transform-utils';
import { contentGlobalSuccess } from '../../../content.error';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import { TableComponent } from '../../../../shared/components/kit/table/table.component';
import { FolderGlobalStore } from '../../../../../store/medicalService/folder.global-store';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { WorkPrintService } from '../../../../../components/medical-service/work/work-print.service';
import { featureOn } from '../../../../../feature-flag';

@Component({
    selector: 'app-specialist-record-list',
    templateUrl: './specialist-record-list.component.html',
    styleUrl: './specialist-record-list.component.scss',
    providers: [SpecialistRecordListStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ButtonComponent,
        TableComponent,
        PrimeTemplate,
    ],
})
export class SpecialistRecordListComponent extends SubscriptionComponent implements OnInit, OnChanges {
    private readonly router = inject(Router);
    private readonly confirm = inject(ConfirmBuilder);


    public readonly store = inject(SpecialistRecordListStore);
    protected readonly folderStore = inject(FolderGlobalStore);
    protected readonly workPrint = inject(WorkPrintService);
    protected readonly extendedWorkExportsEnabled = featureOn.EXTENDED_WORK_EXPORTS;

    @Input() set extraFilters(filters: FilterInput[][]) {
        this._extraFilters = filters;
        this.updateAdditionalFilters();
    }

    @Input() folderId = 0;
    @Input() extraFolderIds: number[] = [];
    public readonly export = input(false);
    public readonly printDateFrom = input<Date | null>(null);
    public readonly printDateTo = input<Date | null>(null);

    protected readonly tableComponent = viewChild(TableComponent);

    protected baseFilters: FilterInput[][] = [
        [
            {
                value: [WorkTypeEnum.SPECIALIST_RECORD],
                column: 'workType',
                operator: 'EQ',
            },
        ],
    ];
    protected additionalFilters: FilterInput[][] = this.baseFilters;

    protected columns: TableColumn[] = [];

    private _extraFilters: FilterInput[][] = [];

    ngOnInit(): void {
        this.columns = [
            folderPersonColumn,
            ...(!this.extendedWorkExportsEnabled && this.export()
                ? [{ name: 'export' }]
                : []),
            dayDateFromColumn,
            dateFromColumn,
            {
                name: 'workData.spr.note',
                dataKey: 'workData.note',
                filterable: true,
                type: TableColumnType.String50,
            },
            {
                name: 'workData.spr.workType',
                dataKey: 'workData.workType',
                filterable: true,
            },
            {
                name: 'workData.spr.contactType',
                dataKey: 'workData.contactType',
                filterable: true,
            },
            {
                name: 'workData.spr.distance',
                dataKey: 'distance',
                filterable: {
                    type: 'number',
                },
            },
            filesColumn,
            usersChipColumn,
        ];
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (this.folderId) {
            this.baseFilters = [
                [
                    {
                        value: [WorkTypeEnum.SPECIALIST_RECORD],
                        column: 'workType',
                        operator: 'EQ',
                    },
                ],
                [
                    {
                        value: [this.folderId.toString(), ...this.extraFolderIds.map((it) => String(it))],
                        column: 'folderId',
                        operator: 'IN',
                    },
                ],
            ];
            this.updateAdditionalFilters();
        }
    }

    protected async detail(item: WorkGridItemObject): Promise<void> {
        await this.router.navigateByUrl(workUri(item, item.serviceId));
    }

    protected delete(input: any): void {
        this.confirm.confirmDelete(
            async () => {
                await this.store.api.deleteWork({ id: input.item.id });
                contentGlobalSuccess.next('common.deleted');
            },
            input,
        );
    }

    private updateAdditionalFilters(): void {
        this.additionalFilters = [...this.baseFilters, ...this._extraFilters];
    }

    protected readonly workHideDelete = workHideDelete(this.store.globalGlobalStore);

    protected printRecords(): void {
        this.workPrint.openBulk({
            selection: this.tableComponent()?.selection(),
            supportedWorkTypes: [WorkTypeEnum.SPECIALIST_RECORD],
            dateFrom: this.printDateFrom(),
            dateTo: this.printDateTo(),
            folderIds: this.folderId ? [this.folderId, ...this.extraFolderIds] : [],
        });
    }

    protected printRecord(item: WorkGridItemObject): void {
        this.workPrint.openRow(item);
    }

}
